import React, { useEffect } from 'react'

import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { Barcode } from 'src/components/Barcode'

import { convertGS1 } from './utils'

const useStyles = makeStyles({
  label: {
    width: '4in',
    height: '6in',
  },
  zoneA: {
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '1.75in',
    fontSize: '14px',
  },
  zoneB: {
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.25in',
    fontSize: '14px',
  },
  zoneC: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.25in',
  },
  zoneD: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    paddingLeft: '3px',
    height: '1in',
    width: '1.75in',
    lineHeight: '1.0',
  },
  zoneE: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '1.75in',
  },
  zoneF: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.25in',
  },
  zoneG: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '1.75in',
  },
  zoneH: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2.25in',
  },
  zoneI: {
    borderTop: '1px solid black',
    height: '1in',
    width: '4in',
  },
  header: {
    fontSize: '12px',
    fontWeight: 'bold',
    paddingLeft: '3px',
  },
  address: {
    lineHeight: '1.0',
    paddingLeft: '10px',
  },
  from: {
    lineHeight: '1.0',
    paddingLeft: '5px',
  },
  carrier: {
    fontWeight: 'bold',
    paddingLeft: '5px',
  },
  to: {
    lineHeight: '1.0',
    fontSize: '16px',
    paddingLeft: '10px',
  },
  container: {
    top: 'auto',
    bottom: 0,
  },
  center: {
    flexGrow: 1,
    textAlign: 'center',
  },
  inlineHeader: {
    fontWeight: 'bold',
    fontSize: '14px',
    paddingLeft: '3px',
  },
})

interface TrueValueLabelProps {
  zoneA: string[]
  zoneB: string[]
  zoneC: string
  zoneD: {
    name: string
    scac: string
    pro: string
    bol: string
  }
  zoneE: string
  zoneF: {
    type: "single" | "mixed",
    sku?: string,
    upc?: string,
    quantity?: number
  }
  zoneI: string
}

interface Props {
  data: TrueValueLabelProps
  onReadyToPrint?: () => void
}

export const TrueValueLabel = (props: Props) => {
  const { data, onReadyToPrint } = props
  const shipToBarcode = { barcode: convertGS1(data.zoneC, 3), type: 'gs1-128', displayName: '' }
  const ssccBarcode = { barcode: convertGS1(data.zoneI, 2), type: 'gs1-128', displayName: '' }
  const classes = useStyles()

  useEffect(() => {
    onReadyToPrint?.()
  }, [onReadyToPrint])

  const MixedZoneF = () => {
    return <Box display="flex" alignItems="center" justifyContent="center" height="100%">
      <Typography variant="h1">MIXED</Typography>
    </Box>
  }

  const SingleZoneF = () => {
    return <Box>
      <Box>
        <span className={classes.inlineHeader}>ITEM #: </span>
        <span>{data.zoneF.sku}</span>
      </Box>
      <Box>
        <span className={classes.inlineHeader}>UPC: </span>
        <span>{data.zoneF.upc}</span>
      </Box>
      <Box>
        <span className={classes.inlineHeader}>QUANTITY: </span>
        <span>{data.zoneF.quantity}</span>
      </Box>
    </Box>
  }

  return (
    <div className={`always-page-break ${classes.label}`}>
      <div className="print-page">
        <Box className={classes.label}>
          <Grid container spacing={0}>
            <Grid item>
              <Box className={classes.zoneA}>
                <Typography className={classes.header}>FROM</Typography>
                {data.zoneA.map((line) => (
                  <Typography key={line} className={classes.from}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneB}>
                <Typography className={classes.header}>TO</Typography>
                {data.zoneB.map((line) => (
                  <Typography key={line} className={classes.to}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneC}>
                <Grid item xs={12}>
                  <Typography className={classes.header}>SHIP TO POSTAL CODE</Typography>
                </Grid>
                <Box display="flex" justifyContent="center">
                  <Typography variant="h6">{shipToBarcode.barcode}</Typography>
                </Box>
                <Box ml={6} mr={6}>
                  <Barcode barcode={shipToBarcode} scale={2} height={9} />
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneD}>
                <Box>
                  <span className={classes.inlineHeader}>CARRIER: </span>
                  <span>{data.zoneD.name}</span>
                </Box>
                <Box>
                  <span className={classes.inlineHeader}>SCAC: </span>
                  <span>{data.zoneD.scac}</span>
                </Box>
                <Box>
                  <span className={classes.inlineHeader}>PRO: </span>
                  <span>{data.zoneD.pro}</span>
                </Box>
                <Box>
                  <span className={classes.inlineHeader}>B/L: </span>
                  <span>{data.zoneD.bol}</span>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneE} pt={1} pl={1}>
                <Typography variant="h5">PO#: {data.zoneE}</Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneF}>
                {data.zoneF.type === "mixed" ? <MixedZoneF /> : <SingleZoneF />}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneG} >
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneH} >
              </Box>
            </Grid>
            <Grid item>
              <Box className={`${classes.zoneI} ${classes.container}`}>
                <Typography className={classes.header}>SSCC-18</Typography>
                <Box ml={5} mr={5}>
                  <Barcode barcode={ssccBarcode} scale={2} height={20} />
                </Box>
                <Box display="flex" justifyContent="center">
                  <Typography variant="h4">{ssccBarcode.barcode}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </div>
    </div>
  )
}
